import React, { useEffect, useState } from "react";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import { ChevronRight, Users, Target, TrendingUp, Heart, Award, Globe, ArrowRight } from "lucide-react";

function HIV() {
  const [isVisible, setIsVisible] = useState({});
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const images = [
    "img/dress/01-min.jpg",
    "img/hiv/0-min.jpg", 
    "img/hiv/1-min.jpg",
    "img/hiv/2-min.jpg"
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    const imageInterval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 5000);

    return () => {
      observer.disconnect();
      clearInterval(imageInterval);
    };
  }, []);

  const stats = [
    { icon: Users, number: "814+", label: "Youths Reached", description: "Lives directly impacted through our programs" },
    { icon: Target, number: "77%", label: "Youth Population", description: "Of Uganda's population under 25 years" },
    { icon: TrendingUp, number: "5", label: "Years Active", description: "Consistent community engagement since 2019" },
    { icon: Heart, number: "480K", label: "At Risk Annually", description: "Young people aged 10-24 newly infected globally" }
  ];

  const achievements = [
    {
      icon: Globe,
      title: "Community Outreach",
      description: "Radio campaigns reaching thousands across Uganda",
      impact: "Widespread awareness"
    },
    {
      icon: Users,
      title: "Educational Workshops", 
      description: "Interactive sessions in schools and communities",
      impact: "Direct knowledge transfer"
    },
    {
      icon: Heart,
      title: "Support & Counseling",
      description: "Comprehensive care for Youth Living With HIV",
      impact: "Holistic wellbeing"
    },
    {
      icon: Award,
      title: "Evidence-Based Approach",
      description: "Data-driven interventions and measurable outcomes",
      impact: "Proven effectiveness"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50 to-pink-50">
      <Header />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden pt-20 sm:pt-32 pb-12 sm:pb-20">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 sm:top-20 left-5 sm:left-10 w-32 sm:w-64 h-32 sm:h-64 bg-red-200/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 sm:bottom-20 right-5 sm:right-10 w-24 sm:w-48 h-24 sm:h-48 bg-pink-200/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 sm:w-96 h-48 sm:h-96 opacity-10">
            <svg viewBox="0 0 200 200" className="w-full h-full text-red-300 animate-spin" style={{animationDuration: '30s'}}>
              <path fill="currentColor" d="M44.7,-76.4C58.8,-69.2,71.8,-57.1,79.6,-41.8C87.4,-26.6,90,-8.2,87.4,9.1C84.8,26.4,77,42.6,63.2,53.8C49.4,65,29.6,71.2,8.9,76.3C-11.8,81.4,-33.4,85.4,-49.8,78.1C-66.2,70.8,-77.4,52.2,-84.1,31.1C-90.8,10,-93,13.5,-89.7,-8.8C-86.4,-31.1,-77.6,-58.2,-61.2,-65.4C-44.8,-72.6,-20.8,-59.9,-0.1,-59.8C20.6,-59.7,30.6,-83.6,44.7,-76.4Z"/>
            </svg>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Content */}
                      <div 
                      id="hero-content"
                      data-animate
                      className={`transform transition-all duration-1000 mt-20 sm:mt-0 text-center sm:text-left px-4 sm:px-0 ${
                      isVisible['hero-content'] ? 'translate-x-0 opacity-100' : 'lg:-translate-x-full opacity-0'
                      }`}
                      >
                      <div className="inline-flex items-center bg-red-100 text-red-800 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6 mx-auto sm:mx-0">
                      <Heart className="w-3 sm:w-4 h-3 sm:h-4 mr-2" />
                      Critical Health Initiative
                      </div>
                      
                      <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 sm:mb-6 leading-tight">
                      <span className="bg-gradient-to-r from-red-600 via-pink-600 to-red-800 bg-clip-text text-transparent">
                        HIV/AIDS Awareness
                      </span>
                      <br />
                      <span className="text-gray-800">Project</span>
                      </h1>
                      
                      <p className="text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed mb-6 sm:mb-8">
                      Empowering Uganda's youth with life-saving knowledge and support. 
                      Together, we're breaking the cycle of HIV transmission through education, 
                      awareness, and comprehensive care.
                      </p>
                      
                      <div className="flex flex-col sm:flex-row gap-4 justify-center sm:justify-start">
                      <button 
                        onClick={() => window.location.href = '/fund-our-work'} 
                        className="bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-2xl font-semibold text-base sm:text-lg transform hover:scale-105 transition-all duration-300 shadow-lg flex items-center justify-center gap-2 w-full sm:w-auto"
                      >
                        Support This Initiative <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5" />
                      </button>
                      </div>
                      </div>

                      {/* Image Carousel */}
            <div 
              id="hero-image"
              data-animate
              className={`transform transition-all duration-1000 delay-300 ${
                isVisible['hero-image'] ? 'translate-x-0 opacity-100' : 'lg:translate-x-full opacity-0'
              }`}
            >
              <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
                <div className="relative h-64 sm:h-80 md:h-96 lg:h-[500px]">
                  <img
                    src={images[currentImageIndex]}
                    alt="HIV Awareness Initiative"
                    className="w-full h-full object-cover transition-opacity duration-1000"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                </div>
                
                {/* Image indicators */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 sm:w-3 h-2 sm:h-3 rounded-full transition-all duration-300 ${
                        index === currentImageIndex ? 'bg-white scale-125' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Section */}
      <div className="py-12 sm:py-20 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div 
            id="stats-header"
            data-animate
            className={`text-center mb-12 sm:mb-16 transform transition-all duration-1000 ${
              isVisible['stats-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 sm:mb-6">
              Our <span className="bg-gradient-to-r from-red-600 to-pink-600 bg-clip-text text-transparent">Impact</span> in Numbers
            </h2>
            <div className="w-24 sm:w-32 h-1 bg-gradient-to-r from-red-500 to-pink-500 rounded-full mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  id={`stat-${index}`}
                  data-animate
                  className={`transform transition-all duration-1000 ${
                    isVisible[`stat-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                  style={{ transitionDelay: `${index * 200}ms` }}
                >
                  <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-red-100">
                    <div className="w-12 sm:w-16 h-12 sm:h-16 bg-gradient-to-r from-red-500 to-pink-500 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 mx-auto">
                      <Icon className="w-6 sm:w-8 h-6 sm:h-8 text-white" />
                    </div>
                    <div className="text-center">
                      <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-800 mb-2">{stat.number}</div>
                      <div className="text-base sm:text-lg font-semibold text-red-600 mb-2">{stat.label}</div>
                      <div className="text-xs sm:text-sm text-gray-600">{stat.description}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* The Challenge Section */}
      <div className="py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div 
              id="challenge-content"
              data-animate
              className={`transform transition-all duration-1000 ${
                isVisible['challenge-content'] ? 'translate-x-0 opacity-100' : 'lg:-translate-x-full opacity-0'
              }`}
            >
              <div className="inline-flex items-center bg-amber-100 text-amber-800 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
                <Target className="w-3 sm:w-4 h-3 sm:h-4 mr-2" />
                The Critical Challenge
              </div>
              
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-6 sm:mb-8 leading-tight">
                Uganda's Youth at a <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">Critical Crossroads</span>
              </h2>
              
              <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-gray-700 leading-relaxed">
                <p>
                  <strong>Uganda has the youngest population in the world</strong>, with 77% being under 25 years. 
                  This demographic represents both our greatest opportunity and our most urgent challenge.
                </p>
                
                <div className="bg-red-50 border-l-4 border-red-500 p-4 sm:p-6 rounded-r-xl sm:rounded-r-2xl">
                  <p className="font-semibold text-red-800 text-sm sm:text-base">
                    HIV prevalence triples from ages 15-19 (1.1%) to ages 20-24 (3.3%), 
                    yet testing rates among younger teens remain critically low at just 47.3%.
                  </p>
                </div>
                
                <p>
                  In 2022 alone, <strong>480,000 young people globally</strong> were newly infected with HIV, 
                  with 140,000 being adolescents. The primary driver? Lack of comprehensive, 
                  accessible AIDS-related information.
                </p>
              </div>
            </div>

            <div 
              id="challenge-visual"
              data-animate
              className={`transform transition-all duration-1000 delay-300 ${
                isVisible['challenge-visual'] ? 'translate-x-0 opacity-100' : 'lg:translate-x-full opacity-0'
              } mt-8 lg:mt-0`}
            >
              <div className="relative">
                <div className="bg-gradient-to-br from-red-600 via-pink-600 to-orange-600 rounded-2xl sm:rounded-3xl p-1 shadow-2xl">
                  <img
                    src="img/hiv/0-min.jpg"
                    alt="Youth engagement"
                    className="w-full h-64 sm:h-80 object-cover rounded-2xl sm:rounded-3xl"
                  />
                </div>
                
                {/* Floating stats */}
                <div className="absolute -bottom-4 sm:-bottom-6 -left-4 sm:-left-6 bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-lg">
                  <div className="text-lg sm:text-2xl font-bold text-red-600">1.1%</div>
                  <div className="text-xs sm:text-sm text-gray-600">Ages 15-19</div>
                </div>
                <div className="absolute -top-4 sm:-top-6 -right-4 sm:-right-6 bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-lg">
                  <div className="text-lg sm:text-2xl font-bold text-red-600">3.3%</div>
                  <div className="text-xs sm:text-sm text-gray-600">Ages 20-24</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Our Approach Section */}
      <div className="py-12 sm:py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div 
            id="approach-header"
            data-animate
            className={`text-center mb-12 sm:mb-16 transform transition-all duration-1000 ${
              isVisible['approach-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 sm:mb-6">
              Our Strategic <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Approach</span>
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Since February 2019, we've developed a comprehensive, evidence-based methodology 
              that delivers measurable results in HIV prevention and support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {achievements.map((achievement, index) => {
              const Icon = achievement.icon;
              return (
                <div
                  key={index}
                  id={`achievement-${index}`}
                  data-animate
                  className={`transform transition-all duration-1000 ${
                    isVisible[`achievement-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                  style={{ transitionDelay: `${index * 200}ms` }}
                >
                  <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 h-full">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 sm:w-16 h-12 sm:h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 sm:w-8 h-6 sm:h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">{achievement.title}</h3>
                        <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 leading-relaxed">{achievement.description}</p>
                        <div className="inline-flex items-center bg-blue-100 text-blue-800 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                          {achievement.impact}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="py-12 sm:py-20 bg-gradient-to-r from-red-600 via-pink-600 to-purple-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div 
            id="cta-content"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['cta-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 sm:mb-8 leading-tight">
              Join Us in Saving Lives
            </h2>
            <p className="text-base sm:text-xl text-white/90 mb-8 sm:mb-12 max-w-3xl mx-auto px-4">
              Your support enables us to reach more youth, provide essential education, 
              and break the cycle of HIV transmission. Together, we can create a healthier future for Uganda.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center px-4">
              <button 
                onClick={() => window.location.href = '/fund-our-work'} 
                className="bg-white text-red-600 px-8 sm:px-10 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl w-full sm:w-auto"
              >
                Fund This Project
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default HIV;